import { Box, BoxProps, Flex, Tooltip } from "@chakra-ui/react";
import React from "react";

import {
  IconButton,
  PlaybackRateMenu,
  SkipBackward15Icon,
  SkipForward15Icon,
} from "../../../components";
import CurrentTime from "../MediaPlayer/CurrentTime";
import { MediaPlayerInterface } from "../MediaPlayer/useMediaPlayer";
import { VideoStateControl } from "../MediaPlayer/useVideoState";
import { AudioControlsV2 } from "./AudioControlsV2";
import {
  CONTROL_ACTIVE_BG,
  CONTROL_HOVER_BG,
  IconPause,
  IconPlay,
} from "./theme";
import VideoFullscreenControlsV2 from "./VideoFullscreenControlsV2";

export type VideoControlsV2Props = BoxProps & {
  player: MediaPlayerInterface;
  isVideoVisible: boolean;
  fullScreenControls?: VideoStateControl;
  pipControls?: VideoStateControl;
  isSmallScreen?: boolean;
  containerWidth?: number;
};

const playbackRateMenuProps = {
  variant: "blackAlpha",
};

const playbackRateMenuButtonProps = {
  borderRadius: "full",
  background: "transparent",
  w: 10,
  h: 10,
  minW: 10,
  _hover: { bg: CONTROL_HOVER_BG },
  _active: { bg: CONTROL_ACTIVE_BG },
};

const VideoControlsV2Component: React.FC<VideoControlsV2Props> = ({
  player,
  isVideoVisible,
  fullScreenControls,
  pipControls,
  isSmallScreen,
  containerWidth,
  ...styles
}) => {
  const {
    muted,
    playbackRate,
    volume,
    playing,
    skipBack,
    skipForward,
    time,
    duration,
    pause,
    play,
    mute,
    setVolume,
    setPlaybackRate,
  } = player;

  return (
    <Flex
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      lineHeight={1}
      height={10}
      {...styles}
    >
      <Flex alignItems="center" gap="1">
        {playing ? (
          <Tooltip label="Pause">
            <IconButton
              icon={<IconPause size={24} />}
              isRound
              aria-label="Pause"
              onClick={pause}
              background="transparent"
              w={10}
              h={10}
              minW={10}
              _hover={{ bg: CONTROL_HOVER_BG }}
              _active={{ bg: CONTROL_ACTIVE_BG }}
            />
          </Tooltip>
        ) : (
          <Tooltip label="Play">
            <IconButton
              icon={<IconPlay size={24} />}
              isRound
              aria-label="Play"
              onClick={play}
              background="transparent"
              w={10}
              h={10}
              minW={10}
              _hover={{ bg: CONTROL_HOVER_BG }}
              _active={{ bg: CONTROL_ACTIVE_BG }}
            />
          </Tooltip>
        )}
        {!(
          containerWidth !== undefined &&
          containerWidth <= 390 &&
          !isVideoVisible
        ) && (
          <>
            <Tooltip label="Skip Backward">
              <IconButton
                background="transparent"
                aria-label="Skip Backward"
                borderRadius="full"
                w={10}
                h={10}
                minW={10}
                _hover={{ bg: CONTROL_HOVER_BG }}
                _active={{ bg: CONTROL_ACTIVE_BG }}
                onClick={() => skipBack(15)}
                icon={
                  <Box
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <SkipBackward15Icon size="24px" color="white" />
                  </Box>
                }
              />
            </Tooltip>
            <Tooltip label="Skip Forward">
              <IconButton
                background="transparent"
                aria-label="Skip Forward"
                w={10}
                h={10}
                minW={10}
                borderRadius="full"
                _hover={{ bg: CONTROL_HOVER_BG }}
                _active={{ bg: CONTROL_ACTIVE_BG }}
                onClick={() => skipForward(15)}
                icon={
                  <Box
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <SkipForward15Icon size="24px" color="white" />
                  </Box>
                }
              />
            </Tooltip>
          </>
        )}
        <PlaybackRateMenu
          playbackRate={playbackRate}
          setPlaybackRate={setPlaybackRate}
          menuProps={playbackRateMenuProps}
          menuButtonProps={playbackRateMenuButtonProps}
        />

        <AudioControlsV2
          setVolume={setVolume}
          mute={mute}
          volume={volume}
          muted={muted}
          isTop={!isVideoVisible}
          horizontal={!isVideoVisible}
        />
        <CurrentTime time={time} duration={duration} marginLeft="3" />
      </Flex>

      {isVideoVisible && (
        <Flex alignItems="center">
          <VideoFullscreenControlsV2
            pipControls={pipControls}
            fullScreenControls={fullScreenControls}
            isSmallScreen={isSmallScreen}
          />
        </Flex>
      )}
    </Flex>
  );
};
VideoControlsV2Component.displayName = "VideoControlsV2";
export const VideoControlsV2 = React.memo(VideoControlsV2Component);
